/**
 * Search Map Alpine Component
 * Renders an interactive Mapbox map for search results with clustered markers
 * - Clusters show as circles with count
 * - Individual markers show price labels
 * - Clicking a marker shows a property card popup
 */

import Alpine from 'alpinejs';
import { createMap, mapboxgl } from '../utils/mapbox.js';
import {
    isValidCoord,
    formatPrice,
    getListingKey,
    getListingDisplayPrice,
    groupListingsByCoordinate,
    calculatePopupAnchor,
    ensurePopupVisible as sharedEnsurePopupVisible,
    POPUP_GAP,
    DEFAULT_MARKER_HEIGHT,
    POPUP_BASE_OPTIONS,
} from '../utils/mapHelpers.js';

// Source and layer IDs
const SOURCE_ID = 'listings';
const CLUSTER_LAYER_ID = 'clusters';
const CLUSTER_COUNT_LAYER_ID = 'cluster-count';

const LOADING_PLACEHOLDER_HTML = `
    <div class="search-map-popup-card">
        <div class="flex items-center justify-center p-8">
            <div class="animate-pulse text-av-warm-grey text-sm">Loading...</div>
        </div>
    </div>`;

Alpine.data('searchMap', () => ({
    map: null,
    popup: null,
    popupCache: new Map(), // Map of listing/marker key -> mapboxgl.Popup instance
    _popupHtmlCache: new Map(), // entryId -> fetched popup HTML string (for carousel slides)
    priceMarkersMap: new Map(), // markerKey -> marker (listingKey for singles, group_xxx for stacks)
    markerGroups: new Map(), // markerKey -> { listings[], lat, lng }
    listingToMarkerKey: new Map(), // listingKey -> markerKey (reverse lookup for hover)
    loaded: false,
    resizeObserver: null,
    updateTimeout: null,
    currentPopupListing: null,
    currentPopupCoordinates: null,
    windowResizeHandler: null,
    pendingFit: null,
    pendingFitId: 0,
    pendingFitInFlight: false,
    pendingListings: null,
    pendingUpdateOptions: null,
    pendingMarkers: false,
    pendingInitialFit: false,
    pendingInitialReveal: false,
    visibilityObserver: null,
    mapHidden: false,

    // Carousel popup state for stacked markers
    _carouselIndex: 0,
    _carouselListings: null,
    _carouselMarkerKey: null,

    // Search-as-you-move state (improved)
    userInteracted: false, // True when user has manually dragged/zoomed map
    programmaticMoveInProgress: false, // True during fitBounds animations
    allowNextBoundsSearch: false, // Single-shot override for special programmatic moves
    lastBoundsRequestId: 0, // Monotonically increasing counter for request ordering
    lastBounds: null, // Previous bounds for meaningful change detection
    hasInitialFit: false, // Track if we've performed the initial bounds fit

    // New Interactive controls
    searchAsIMove: true, // Default to auto-search
    showSearchHereButton: false, // "Search this area" pill visibility

    // Default map center (US-centric)
    defaultCenter: [-98.5795, 39.8283],
    defaultZoom: 4,

    // Cluster settings
    clusterRadius: 50,
    clusterMaxZoom: 14,

    init() {
        this.$nextTick(() => {
            this.initMap();
        });

        // Add window resize handler for popup width
        this.windowResizeHandler = () => this.updatePopupWidth();
        window.addEventListener('resize', this.windowResizeHandler);

        // Watch for card hover to highlight corresponding marker
        this.$watch('$store.search.cardHoveredListingId', (newId, oldId) => {
            this.updateMarkerHighlight(oldId, false);
            this.updateMarkerHighlight(newId, true);
        });
    },

    updateMarkerHighlight(listingId, isHighlighted) {
        if (!listingId) return;

        // Resolve listingId to markerKey (handles both singles and groups)
        const markerKey = this.listingToMarkerKey.get(listingId) || listingId;
        const marker = this.priceMarkersMap.get(markerKey);
        if (!marker) return;

        const el = marker.getElement();
        const span = el?.querySelector('span');
        if (!span) return;

        const isGroup = markerKey.startsWith('group_');
        const baseClass = isGroup ? 'bg-av-dark-brown/70' : 'bg-av-dark-brown/60';

        if (isHighlighted) {
            span.classList.remove(baseClass);
            span.classList.add('bg-av-dark-brown', 'scale-110', 'shadow-av-2');
        } else {
            span.classList.remove('bg-av-dark-brown', 'scale-110', 'shadow-av-2');
            span.classList.add(baseClass);
        }
    },

    toNumber(value) {
        if (value === null || value === undefined) return null;
        if (typeof value === 'string' && value.trim() === '') return null;
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
    },

    async initMap() {
        // Avoid initializing Mapbox when the container is hidden or 0x0.
        // This prevents the internal transform from becoming NaN.
        await this.waitForStableSize();

        let mapOptions = {
            container: this.$refs.map,
            center: this.defaultCenter,
            zoom: this.defaultZoom,
            interactive: true,
        };

        // Calculate initial bounds if we have results on load
        const listings = this.$store.search?.results?.mapPoints || [];
        const validListings = listings.filter((l) => {
            const lat = this.toNumber(l.lat);
            const lng = this.toNumber(l.lng);
            return isValidCoord(lng, lat);
        });

        const initialFeatures = validListings.map((listing) => {
            const lat = this.toNumber(listing.lat);
            const lng = this.toNumber(listing.lng);
            return {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lng, lat],
                },
            };
        });

        // Hide map until we apply the first fit to avoid a "US flash"
        if (this.$refs.map) {
            this.$refs.map.style.opacity = '0';
        }

        // Create map using shared utility
        this.map = createMap(this.$refs.map, mapOptions);

        if (!this.map) return;

        // Mark as loaded when map is ready
        this.map.on('load', () => {
            this.loaded = true;

            // Resize map to fit container after it's fully loaded
            if (this.isContainerUsable()) {
                this.map.resize();
            }

            // Setup clustering layers
            this.setupClusterLayers();

            // Initial marker update if results exist
            // We queue a fit to avoid race conditions with container sizing/layout shifts.
            const seedListings = (this.pendingListings && this.pendingListings.length)
                ? this.pendingListings
                : (this.$store.search?.results?.mapPoints || []);

            if (seedListings.length) {
                this.updateMarkers({ animate: false, skipFit: true });
                const seedFeatures = seedListings
                    .map((listing) => {
                        const lat = this.toNumber(listing.lat);
                        const lng = this.toNumber(listing.lng);
                        if (!isValidCoord(lng, lat)) return null;
                        return {
                            type: 'Feature',
                            geometry: {
                                type: 'Point',
                                coordinates: [lng, lat],
                            },
                        };
                    })
                    .filter(Boolean);
                if (seedFeatures.length) {
                    this.queueFitBounds(seedFeatures, { animate: false, isInitial: true });
                    this.hasInitialFit = true;
                } else if (this.$refs.map) {
                    // No valid coords yet; keep hidden until the first real fit
                    this.pendingInitialReveal = true;
                }
            } else if (this.$refs.map) {
                // No initial results yet (likely loading) — keep hidden until first fit
                this.pendingInitialReveal = true;
            }
        });

        // Watch for changes to map points (all results, not just current page)
        this.$watch('$store.search.results.mapPoints', (val) => {
            if (window.__AVARI_DEBUG_MAP_SEARCH || true) {
                console.log('[MAP] Watcher triggered (mapPoints)', val?.length);
            }
            if (this.loaded) {
                const lastSource = this.$store.search?._lastSearchSource || 'user';
                const isFirstFit = !this.hasInitialFit && this.pendingInitialReveal;
                // Jump on first load, animate on other non-bounds searches
                const shouldAnimate = !isFirstFit && lastSource !== 'bounds';

                const didUpdate = this.updateMarkers({
                    animate: shouldAnimate,
                    isInitial: isFirstFit,
                });

                if (!shouldAnimate || isFirstFit) {
                    if (didUpdate) {
                        if (isFirstFit) {
                            this.pendingInitialReveal = false;
                        }
                        this.hasInitialFit = true;
                    } else {
                        this.pendingInitialFit = true;
                    }
                }

                // CRITICAL UX RULE: Never auto-fit if this was a bounds-based search
                // Only auto-fit for destination/filter searches
                if (!this.$store.search?.boundsSearchEnabled) {
                    this.userInteracted = false;
                }
            } else {
                this.pendingListings = val;
            }
        });

        // Watch for price updates (e.g. date change with same listings)
        this.$watch('$store.search.results.pricedNights', () => {
            if (this.loaded) {
                const mapPoints = this.$store.search?.results?.mapPoints || [];
                this.createPriceMarkers(mapPoints);
            }
        });

        // Track user interaction with map
        this.map.on('dragstart', () => {
            this.userInteracted = true;
        });

        this.map.on('zoomstart', (e) => {
            // Only count as interaction if user-initiated (not programmatic)
            if (e.originalEvent) {
                this.userInteracted = true;
            }
        });

        // Update price marker visibility when map moves/zooms (debounced)
        this.map.on('moveend', () => {
            // Skip if not loaded
            if (!this.loaded) {
                return;
            }

            // Handle programmatic moves
            if (this.programmaticMoveInProgress) {
                // Allow single-shot override (e.g., "Search this area" button)
                if (!this.allowNextBoundsSearch) {
                    return; // Skip bounds search for normal programmatic moves
                }
                // Consume the single-shot flag
                this.allowNextBoundsSearch = false;
            }

            this.debouncedUpdateVisibility();

            // Auto-search after user stops moving map (debounced)
            if (this.userInteracted) {
                const currentBounds = this.map.getBounds();
                const isLoading = this.$store.search?.ui?.isLoading;
                const hasResults = this.$store.search?.results?.mapPoints?.length > 0;
                const lastSource = this.$store.search?._lastSearchSource;

                // Check significance first to avoid unnecessary logic if bounds barely moved
                const isSignificant = this.boundsChangedSignificantly(currentBounds);

                if (!isSignificant) {
                    return;
                }

                // If "Search as I move" is OFF, show the button instead of searching
                if (!this.searchAsIMove) {
                    this.showSearchHereButton = true;
                    // We update lastBounds so that if they toggle it back ON, we don't re-trigger for this same move
                    // (But we already updated it in boundsChangedSignificantly, so we're good)
                    return;
                }

                // Hide button if we are auto-searching (just in case)
                this.showSearchHereButton = false;

                // Don't search if already loading (unless we really want to cancel previous? keeping existing logic for now)
                if (isLoading) {
                    return;
                }

                // Don't search if we're showing bounds-based empty results and bounds haven't changed (handled by isSignificant)
                if (!hasResults && lastSource === 'bounds' && !isSignificant) {
                    return;
                }

                this.triggerBoundsSearch();
            }
        });

        // Also update on source data changes (when clustering recalculates)
        this.map.on('sourcedata', (e) => {
            if (e.sourceId === SOURCE_ID && e.isSourceLoaded && this.loaded) {
                this.debouncedUpdateVisibility();
            }
        });

        // Observe container resize and update map accordingly
        this.resizeObserver = new ResizeObserver(() => {
            if (this.map) {
                if (this.isContainerUsable()) {
                    this.map.resize();
                    if (this.pendingFit) {
                        this.flushPendingFit();
                    }
                    this.flushPendingMarkers();
                }
            }
        });
        this.resizeObserver.observe(this.$refs.mapContainer);

        // Observe visibility changes to prevent NaN transforms when hidden
        this.visibilityObserver = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (!this.map) return;

            if (entry && entry.isIntersecting && this.isContainerUsable()) {
                this.mapHidden = false;
                const canvas = this.map.getCanvas();
                if (canvas) canvas.style.pointerEvents = '';
                this.map.resize();
                this.map.triggerRepaint();
                if (this.pendingFit) {
                    this.flushPendingFit();
                }
                this.flushPendingMarkers();
            } else {
                this.mapHidden = true;
                const canvas = this.map.getCanvas();
                if (canvas) canvas.style.pointerEvents = 'none';
            }
        }, { threshold: 0.01 });
        this.visibilityObserver.observe(this.$refs.map);
    },

    setupClusterLayers() {
        // Add empty GeoJSON source with clustering enabled
        this.map.addSource(SOURCE_ID, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [],
            },
            cluster: true,
            clusterRadius: this.clusterRadius,
            clusterMaxZoom: this.clusterMaxZoom,
        });

        // Cluster circles layer
        this.map.addLayer({
            id: CLUSTER_LAYER_ID,
            type: 'circle',
            source: SOURCE_ID,
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': '#291E03',
                'circle-opacity': 0.85,
                'circle-radius': [
                    'step',
                    ['get', 'point_count'],
                    20,  // radius for count < 10
                    10, 25,  // radius for count >= 10
                    30, 30,  // radius for count >= 30
                ],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#FCF8EE',
            },
        });

        // Cluster count text layer
        this.map.addLayer({
            id: CLUSTER_COUNT_LAYER_ID,
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['has', 'point_count'],
            layout: {
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
            },
            paint: {
                'text-color': '#FCF8EE',
            },
        });

        // Click handler for clusters - zoom in
        this.map.on('click', CLUSTER_LAYER_ID, (e) => {
            const features = this.map.queryRenderedFeatures(e.point, {
                layers: [CLUSTER_LAYER_ID],
            });
            const clusterId = features[0].properties.cluster_id;

            this.map.getSource(SOURCE_ID).getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) return;

                this.map.easeTo({
                    center: features[0].geometry.coordinates,
                    zoom: zoom,
                });
            });
        });

        // Change cursor on cluster hover
        this.map.on('mouseenter', CLUSTER_LAYER_ID, () => {
            this.map.getCanvas().style.cursor = 'pointer';
        });
        this.map.on('mouseleave', CLUSTER_LAYER_ID, () => {
            this.map.getCanvas().style.cursor = '';
        });

        // Close popup when clicking empty map area.
        // We use a manual handler instead of Mapbox's closeOnClick because on mobile,
        // touch events can fire both the marker click (opening) and a map click (closing)
        // on the same event cycle. The _popupJustOpened guard prevents that race.
        this.map.on('click', () => {
            if (!this.popup || this._popupJustOpened) return;
            this.popup.remove();
        });
    },

    debouncedUpdateVisibility() {
        // Debounce visibility updates to prevent flickering
        if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
        }
        this.updateTimeout = setTimeout(() => {
            this.updateMarkerVisibility();
        }, 50);
    },

    emitBounds() {
        const bounds = this.map.getBounds();
        this.$store.search?.setBounds?.({
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest(),
        });
    },

    /**
     * Check if bounds changed meaningfully to prevent micro-move searches
     * Returns true if:
     * - No previous bounds (first interaction)
     * - Bounds changed by > 10% in any direction
     */
    boundsChangedSignificantly(newBounds) {
        if (!this.lastBounds) {
            // First interaction - update and allow
            this.lastBounds = newBounds;
            return true;
        }

        const old = this.lastBounds;
        const latDelta = Math.abs(newBounds.getNorth() - old.getNorth()) + Math.abs(newBounds.getSouth() - old.getSouth());
        const lngDelta = Math.abs(newBounds.getEast() - old.getEast()) + Math.abs(newBounds.getWest() - old.getWest());

        const latSpan = old.getNorth() - old.getSouth();
        const lngSpan = old.getEast() - old.getWest();

        // Changed by more than 10% of span in either direction
        const latChangePercent = latDelta / latSpan;
        const lngChangePercent = lngDelta / lngSpan;

        const threshold = 0.1; // 10%
        const isSignificant = latChangePercent > threshold || lngChangePercent > threshold;

        // Only update lastBounds when change is significant
        // This allows small moves to accumulate until they hit the threshold
        if (isSignificant) {
            this.lastBounds = newBounds;
        }

        return isSignificant;
    },

    triggerBoundsSearch() {
        // Increment request ID for "latest request wins" pattern
        this.lastBoundsRequestId++;
        const requestId = this.lastBoundsRequestId;

        if (window.__AVARI_DEBUG_MAP_SEARCH) {
            console.log('[MAP] Triggering bounds search, requestId:', requestId);
        }

        this.emitBounds();
        this.$store.search?.searchInBounds?.(requestId);
    },

    /**
     * Manual trigger for "Search this area" button
     */
    searchHere() {
        this.showSearchHereButton = false;
        // Force a search even if bounds haven't "changed" significantly (user explicit action)
        // But triggerBoundsSearch relies on emitBounds which uses current map bounds.
        this.triggerBoundsSearch();
    },

    /**
     * Cancel pending bounds search (no-op now that debounce is removed)
     * Kept for API compatibility
     */
    cancelPendingBoundsSearch() {
        // No-op - searches are now immediate
    },

    isListVisible() {
        const listContainer = document.querySelector('[x-data="searchMap"]')?.closest('.grid')?.querySelector('ul');
        return listContainer && listContainer.offsetParent !== null; // offsetParent is null if hidden
    },

    getCardWidth() {
        // Find the first property card in search results to match its width
        const firstCard = document.querySelector('[x-data="searchMap"]')
            ?.closest('.grid')
            ?.querySelector('ul li > a');

        // If card exists and is visible, use its width
        if (firstCard && firstCard.offsetWidth > 0) {
            return firstCard.offsetWidth;
        }

        // Fallback width if list is hidden (e.g. mobile "Search by map" mode)
        return 280;
    },

    /**
     * Hydrate a server-rendered popup card with dynamic data.
     * Called after every popup show (new or cached) to ensure price, wishlist, and links are current.
     */
    hydratePopup(listing) {
        const popupEl = this.popup?.getElement();
        if (!popupEl) return;

        // Set card width to match the property cards in the list
        const cardWidth = this.getCardWidth();
        const card = popupEl.querySelector('.search-map-popup-card');
        if (card && cardWidth) {
            card.style.width = `${cardWidth}px`;
        }

        // Update price (reactive when dates are selected)
        const priceEl = popupEl.querySelector('[data-popup-price]');
        if (priceEl) {
            priceEl.textContent = formatPrice(getListingDisplayPrice(listing, this.$store.search));
        }

        const priceLabelEl = popupEl.querySelector('[data-popup-price-label]');
        if (priceLabelEl) {
            priceLabelEl.textContent = this.$store.search?.priceLabel || '/ night';
        }

        // Update wishlist state
        const listingId = listing.id || listing.guestyListingId;
        const heartBtn = popupEl.querySelector('.search-map-popup-heart');
        if (heartBtn) {
            const isFavorited = this.$store.wishlist?.has?.(listingId);
            heartBtn.classList.toggle('is-active', !!isFavorited);
        }

        // Append search query string to links
        const suffix = window.location.search;
        if (suffix) {
            popupEl.querySelectorAll('[data-popup-link]').forEach((link) => {
                const base = link.getAttribute('href').split('?')[0];
                link.setAttribute('href', base + suffix);
            });
        }
    },

    hoverCloseTimeout: null,

    schedulePopupClose() {
        if (this.hoverCloseTimeout) clearTimeout(this.hoverCloseTimeout);
        this.hoverCloseTimeout = setTimeout(() => {
            if (this.popup) {
                this.popup.remove();
                this.popup = null;
                this.currentPopupListing = null;
            }
        }, 300); // 300ms safety delay
    },

    cancelPopupClose() {
        if (this.hoverCloseTimeout) {
            clearTimeout(this.hoverCloseTimeout);
            this.hoverCloseTimeout = null;
        }
    },

    /**
     * Resolve popupHtml for a listing.
     * Checks the listing itself, then current-page listings, returns null if not found.
     */
    _getPopupHtml(listing) {
        if (listing.popupHtml) return listing.popupHtml;

        // Check if a matching listing exists on the current page
        const listingKey = getListingKey(listing);
        const pageListing = (this.$store.search?.results?.listings || [])
            .find(l => getListingKey(l) === listingKey);
        if (pageListing?.popupHtml) return pageListing.popupHtml;

        return null;
    },

    /**
     * Attach carousel navigation + heart listeners to a group popup.
     * Called on every show (cache-hit or new) since Mapbox recreates the DOM wrapper.
     */
    _attachCarouselListeners(popupInstance) {
        const popupEl = popupInstance.getElement();
        if (!popupEl) return;

        popupEl.style.pointerEvents = 'auto';

        popupEl.addEventListener('mouseenter', () => {
            this.cancelPopupClose();
        });

        // Carousel navigation (delegated)
        popupEl.addEventListener('click', (e) => {
            const prevBtn = e.target.closest('[data-carousel-prev]');
            const nextBtn = e.target.closest('[data-carousel-next]');
            if (prevBtn) { e.stopPropagation(); this.carouselPrev(); }
            if (nextBtn) { e.stopPropagation(); this.carouselNext(); }
        });

        // Heart buttons (delegated for all slides)
        popupEl.addEventListener('click', (e) => {
            const heartBtn = e.target.closest('.search-map-popup-heart');
            if (!heartBtn) return;
            e.preventDefault();
            e.stopPropagation();
            const listingId = parseInt(heartBtn.dataset.listingId, 10);
            if (!this.$store.user?.isFetched) return;
            if (!this.$store.user?.isLoggedIn) {
                this.$store.modals?.open?.('sign-in-modal');
                return;
            }
            this.$store.wishlistModal?.open?.(listingId);
        });
    },

    /**
     * Attach event listeners to a newly created popup element.
     */
    _attachPopupListeners(popupInstance, listingKey) {
        let popupEl = null;
        try {
            popupEl = popupInstance.getElement();
            if (popupEl) {
                popupEl.style.pointerEvents = 'auto';
            }

            popupEl.addEventListener('mouseenter', () => {
                this.cancelPopupClose();
            });

            const heartBtn = popupEl.querySelector('.search-map-popup-heart');
            if (heartBtn) {
                heartBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const listingId = parseInt(heartBtn.dataset.listingId, 10);

                    if (!this.$store.user?.isFetched) return;
                    if (!this.$store.user?.isLoggedIn) {
                        this.$store.modals?.open?.('sign-in-modal');
                        return;
                    }
                    this.$store.wishlistModal?.open?.(listingId);
                });
            }
        } catch (err) {
            console.error('Error attaching popup handlers:', err);
        }
    },

    /**
     * Create a new popup instance with close event handling and add to cache.
     */
    _createPopupInstance(html, listingKey, anchor, popupOffset, coordinates) {
        const popup = new mapboxgl.Popup({
            ...POPUP_BASE_OPTIONS,
            anchor,
            offset: popupOffset,
        })
            .setLngLat(coordinates)
            .setHTML(html)
            .addTo(this.map);

        this.popupCache.set(listingKey, popup);

        popup.on('close', () => {
            if (this._suppressClose) return;
            if (this.currentPopupListing) {
                const closedKey = getListingKey(this.currentPopupListing);
                if (closedKey === listingKey) {
                    this.popup = null;
                    this.currentPopupListing = null;
                    this.currentPopupCoordinates = null;
                }
            }
        });

        this._attachPopupListeners(popup, listingKey);
        return popup;
    },

    showPopup(listing, coordinates, fromHover = false) {
        // Cancel any pending close since we are showing a popup (new or same)
        this.cancelPopupClose();

        const listingKey = getListingKey(listing);
        const currentKey = this.currentPopupListing ? getListingKey(this.currentPopupListing) : null;

        // Optimized: If popup is already open for this listing, don't re-render
        if (this.popup && currentKey === listingKey) {
            this.updatePopupWidth();
            if (fromHover) return;
            // If it's a click, fall through to scroll-to-view logic below
        } else {
            // New listing (or replacing current)

            // 1. Remove current popup
            if (this.popup) {
                this.popup.remove();
            }

            // Clear carousel state if switching from a group popup
            this._carouselListings = null;
            this._carouselMarkerKey = null;

            // Store current listing for resize updates
            this.currentPopupListing = listing;
            this.currentPopupCoordinates = coordinates;

            const markerInstance = this.priceMarkersMap.get(listingKey);
            const markerHeight = markerInstance?.getElement()?.offsetHeight || DEFAULT_MARKER_HEIGHT;

            this._popupGap = POPUP_GAP;
            this._popupMarkerHeight = markerHeight;

            const { anchor, offset: popupOffset } = calculatePopupAnchor({
                map: this.map,
                coordinates,
                markerHeight,
                gap: POPUP_GAP,
                lastPopupHeight: this._lastPopupHeight,
            });

            // 2. Check cache first
            if (this.popupCache.has(listingKey)) {
                this.popup = this.popupCache.get(listingKey);
                this.popup.options.anchor = anchor;
                this.popup.options.offset = popupOffset;
                this.popup.setLngLat(coordinates).addTo(this.map);
                this.hydratePopup(listing);
            } else {
                // 3. Try to get popup HTML (from listing or current page)
                const popupHtml = this._getPopupHtml(listing);

                if (popupHtml) {
                    // HTML available — create popup immediately
                    this.popup = this._createPopupInstance(popupHtml, listingKey, anchor, popupOffset, coordinates);
                    this.hydratePopup(listing);
                } else {
                    // No HTML available — show loading placeholder, then lazy-fetch
                    this.popup = this._createPopupInstance(LOADING_PLACEHOLDER_HTML, listingKey, anchor, popupOffset, coordinates);

                    // Fetch real popup HTML
                    const entryId = listing.id;
                    this.$store.search.fetchPopup(entryId).then((html) => {
                        // Verify popup is still open for this listing
                        if (!this.currentPopupListing || getListingKey(this.currentPopupListing) !== listingKey) return;

                        // Remove the loading popup from cache and DOM
                        this.popupCache.delete(listingKey);
                        this.popup.remove();

                        // Create real popup
                        this.popup = this._createPopupInstance(html, listingKey, anchor, popupOffset, coordinates);
                        this.hydratePopup(listing);

                        // Ensure visible after content loads
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                if (this.popup) {
                                    this.ensurePopupVisible(coordinates);
                                }
                            });
                        });
                    }).catch((err) => {
                        console.error('Failed to load popup:', err);
                        // Remove broken loading popup
                        if (this.currentPopupListing && getListingKey(this.currentPopupListing) === listingKey) {
                            this.popupCache.delete(listingKey);
                            if (this.popup) this.popup.remove();
                            this.popup = null;
                            this.currentPopupListing = null;
                            this.currentPopupCoordinates = null;
                        }
                    });
                }
            }

            // Prevent the manual map.on('click') handler from closing the popup
            this._popupJustOpened = true;
            setTimeout(() => { this._popupJustOpened = false; }, 0);

            // Verify popup position after render
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (this.popup) {
                        this.ensurePopupVisible(coordinates);
                    }
                });
            });
        }

        // Scroll corresponding list card into view - ONLY if not triggered by hover
        if (!fromHover) {
            const cardId = `listing-card-${listingKey}`;
            const cardEl = document.getElementById(cardId);

            if (cardEl) {
                cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

                if (this.$store.search.hoveredListingId !== listingKey) {
                    this.$store.search.hoveredListingId = listingKey;
                    setTimeout(() => {
                        if (this.$store.search.hoveredListingId === listingKey) {
                            this.$store.search.hoveredListingId = null;
                        }
                    }, 1500);
                }
            }
        }
    },


    ensurePopupVisible(coordinates) {
        if (!this.map || !this.popup) return;

        const { flipped, measuredHeight } = sharedEnsurePopupVisible({
            map: this.map,
            popup: this.popup,
            coordinates,
            gap: this._popupGap,
            markerHeight: this._popupMarkerHeight,
            setSuppressClose: (val) => { this._suppressClose = val; },
            onPan: () => {
                this.programmaticMoveInProgress = true;
                this.map.once('moveend', () => {
                    this.programmaticMoveInProgress = false;
                });
            },
        });

        // Flip does remove() + addTo() which recreates the DOM wrapper,
        // destroying all event listeners. Re-attach them.
        if (flipped) {
            if (this._carouselListings) {
                this._attachCarouselListeners(this.popup);
            } else if (this.currentPopupListing) {
                this._attachPopupListeners(this.popup, getListingKey(this.currentPopupListing));
            }
        }

        if (measuredHeight > 0) {
            this._lastPopupHeight = measuredHeight;
        }
    },

    updatePopupWidth() {
        if (!this.popup || !this.currentPopupListing) return;

        const cardWidth = this.getCardWidth();
        // Update all popup cards (handles both single and carousel)
        const popupCards = this.popup.getElement()?.querySelectorAll('.search-map-popup-card');
        if (popupCards?.length && cardWidth) {
            popupCards.forEach((card) => {
                card.style.width = `${cardWidth}px`;
            });
            if (this.currentPopupCoordinates) {
                this.popup.setLngLat(this.currentPopupCoordinates);
            }
        }
    },

    createPriceMarkers(listings) {
        // Clear popup caches when listings change
        this.popupCache.clear();
        this._popupHtmlCache.clear();

        // Group listings by coordinate proximity
        const { markerGroups, listingToMarkerKey } = groupListingsByCoordinate(listings, this.toNumber);
        this.markerGroups = markerGroups;
        this.listingToMarkerKey = listingToMarkerKey;

        // Remove stale markers whose key is no longer present
        for (const [key, marker] of this.priceMarkersMap) {
            if (!markerGroups.has(key)) {
                marker.remove();
                this.priceMarkersMap.delete(key);
            }
        }

        // Create or update markers for each group
        for (const [markerKey, group] of markerGroups) {
            const { listings: groupListings, lat, lng } = group;
            const isGroup = markerKey.startsWith('group_');

            if (this.priceMarkersMap.has(markerKey)) {
                // Update existing marker
                const existingMarker = this.priceMarkersMap.get(markerKey);
                const el = existingMarker.getElement();
                if (isGroup) {
                    // Update count
                    const span = el.querySelector('span');
                    if (span) span.textContent = groupListings.length;
                } else {
                    // Update price for single listing
                    const span = el.querySelector('span');
                    if (span) {
                        span.textContent = formatPrice(getListingDisplayPrice(groupListings[0], this.$store.search)) || '\u00A0';
                    }
                }
                continue;
            }

            // Create new marker
            const el = isGroup
                ? this.createStackedMarkerElement(markerKey, groupListings, [lng, lat])
                : this.createPriceMarkerElement(groupListings[0]);

            const marker = new mapboxgl.Marker({
                element: el,
                anchor: 'bottom',
            })
                .setLngLat([lng, lat])
                .addTo(this.map);

            this.priceMarkersMap.set(markerKey, marker);
        }
    },


    createPriceMarkerElement(listing) {
        const el = document.createElement('div');
        el.className = 'search-map-price-marker';
        el.innerHTML = `
            <span class="bg-av-dark-brown/60 min-w-8 flex items-center justify-center text-av-cream border border-white py-1 px-1.75 text-[0.875rem] tracking-wide font-semibold rounded-full whitespace-nowrap transition-all duration-200 hover:bg-av-dark-brown hover:text-white">
                ${formatPrice(getListingDisplayPrice(listing, this.$store.search)) || '&nbsp;' }
            </span>
        `;

        const listingKey = getListingKey(listing);
        el.dataset.listingId = listingKey;

        // Add click handler
        el.addEventListener('click', (e) => {
            e.stopPropagation();
            const lat = this.toNumber(listing.lat);
            const lng = this.toNumber(listing.lng);
            if (isValidCoord(lng, lat)) {
                this.showPopup(listing, [lng, lat]);
            }
        });

        // Add hover handlers (card highlighting OR popup showing)
        // Only on devices that support hover to prevent "stuck" states on touch
        if (window.matchMedia('(hover: hover)').matches) {
            el.addEventListener('mouseenter', () => {
                if (this.$store.search) {
                    this.$store.search.hoveredListingId = listingKey;
                }
            });

            el.addEventListener('mouseleave', () => {
                if (this.$store.search) {
                    this.$store.search.hoveredListingId = null;
                }
            });
        }

        return el;
    },

    createStackedMarkerElement(markerKey, listings, coordinates) {
        const el = document.createElement('div');
        el.className = 'search-map-stacked-marker';
        el.innerHTML = `
            <span class="bg-av-dark-brown/70 text-av-cream border border-av-cream flex items-center justify-center min-w-8 h-8 rounded-full text-[0.875rem] font-semibold transition-all duration-200 hover:bg-av-dark-brown hover:text-white">
                ${listings.length}
            </span>
        `;
        el.dataset.markerKey = markerKey;

        el.addEventListener('click', (e) => {
            e.stopPropagation();
            const group = this.markerGroups.get(markerKey);
            if (!group) return;
            this.showGroupPopup(markerKey, group.listings, [group.lng, group.lat]);
        });

        if (window.matchMedia('(hover: hover)').matches) {
            el.addEventListener('mouseenter', () => {
                const group = this.markerGroups.get(markerKey);
                if (this.$store.search && group?.listings[0]) {
                    this.$store.search.hoveredListingId = getListingKey(group.listings[0]);
                }
            });
            el.addEventListener('mouseleave', () => {
                if (this.$store.search) {
                    this.$store.search.hoveredListingId = null;
                }
            });
        }

        return el;
    },

    /**
     * Build carousel HTML with a single slide slot + nav bar.
     * The slot is swapped dynamically by updateCarouselSlide().
     */
    buildCarouselHtml(listing, index, total) {
        const slideContent = this._getSlideContent(listing);

        return `<div class="search-map-carousel">
            <div class="search-map-carousel-slot">${slideContent}</div>
            <div class="search-map-carousel-nav">
                <button type="button" class="search-map-carousel-btn" data-carousel-prev aria-label="Previous listing">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
                <span class="search-map-carousel-counter">${index + 1} / ${total}</span>
                <button type="button" class="search-map-carousel-btn" data-carousel-next aria-label="Next listing">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
            </div>
        </div>`;
    },

    /**
     * Get the HTML content for a single slide — cached HTML, inline popupHtml, or loading placeholder.
     */
    _getSlideContent(listing) {
        // 1. Check the per-entry HTML cache (populated by fetch)
        if (this._popupHtmlCache.has(listing.id)) {
            return this._popupHtmlCache.get(listing.id);
        }
        // 2. Check inline popupHtml (from page results)
        const inlineHtml = this._getPopupHtml(listing);
        if (inlineHtml) {
            this._popupHtmlCache.set(listing.id, inlineHtml);
            return inlineHtml;
        }
        // 3. Loading placeholder
        return LOADING_PLACEHOLDER_HTML;
    },

    showGroupPopup(markerKey, listings, coordinates) {
        this.cancelPopupClose();

        // Safety limit: if an API bug results in hundreds of listings at the exact same 
        // coordinate, cap the popup carousel at 30 to prevent the browser from freezing.
        if (listings.length > 30) {
            listings = listings.slice(0, 30);
        }

        // If already open for this group, just cancel close
        if (this.popup && this._carouselMarkerKey === markerKey) {
            this.updatePopupWidth();
            return;
        }

        // Remove current popup
        if (this.popup) {
            this.popup.remove();
        }

        // Set carousel state
        this._carouselIndex = 0;
        this._carouselListings = listings;
        this._carouselMarkerKey = markerKey;

        // Store for resize/reposition
        this.currentPopupListing = listings[0];
        this.currentPopupCoordinates = coordinates;

        // Measure marker height for offset
        const markerInstance = this.priceMarkersMap.get(markerKey);
        const markerHeight = markerInstance?.getElement()?.offsetHeight || DEFAULT_MARKER_HEIGHT;
        this._popupGap = POPUP_GAP;
        this._popupMarkerHeight = markerHeight;

        // Pre-calculate anchor
        const { anchor, offset: popupOffset } = calculatePopupAnchor({
            map: this.map,
            coordinates,
            markerHeight,
            gap: POPUP_GAP,
            lastPopupHeight: this._lastPopupHeight,
        });

        // Always create a fresh popup for group carousels (virtual single-slot)
        const html = this.buildCarouselHtml(listings[0], 0, listings.length);
        this.popup = new mapboxgl.Popup({
            ...POPUP_BASE_OPTIONS,
            anchor,
            offset: popupOffset,
        })
            .setLngLat(coordinates)
            .setHTML(html)
            .addTo(this.map);

        this.popup.on('close', () => {
            if (this._suppressClose) return;
            if (this._carouselMarkerKey === markerKey) {
                this.popup = null;
                this.currentPopupListing = null;
                this.currentPopupCoordinates = null;
                this._carouselListings = null;
                this._carouselMarkerKey = null;
            }
        });

        // Attach carousel + heart listeners (fresh every show)
        this._attachCarouselListeners(this.popup);

        // Render the first slide (from cache or skeleton → fetch)
        this.updateCarouselSlide();

        // Batch-prefetch remaining slides in a single request
        this._batchPrefetchRemaining(listings, markerKey);

        // Prevent same-cycle close
        this._popupJustOpened = true;
        setTimeout(() => { this._popupJustOpened = false; }, 0);

        // Verify popup position
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (this.popup) {
                    this.ensurePopupVisible(coordinates);
                }
            });
        });
    },

    carouselPrev() {
        if (!this._carouselListings) return;
        const len = this._carouselListings.length;
        this._carouselIndex = (this._carouselIndex - 1 + len) % len;
        this.updateCarouselSlide();
    },

    carouselNext() {
        if (!this._carouselListings) return;
        const len = this._carouselListings.length;
        this._carouselIndex = (this._carouselIndex + 1) % len;
        this.updateCarouselSlide();
    },

    /**
     * Swap the single carousel slot to show the current slide.
     * Uses cached HTML or skeleton, then fetches if needed.
     */
    updateCarouselSlide() {
        if (!this.popup || !this._carouselListings) return;

        const popupEl = this.popup.getElement();
        if (!popupEl) return;

        const index = this._carouselIndex;
        const listing = this._carouselListings[index];
        const total = this._carouselListings.length;

        // Swap slot content
        const slot = popupEl.querySelector('.search-map-carousel-slot');
        if (slot) {
            slot.innerHTML = this._getSlideContent(listing);
        }

        // Update counter
        const counter = popupEl.querySelector('.search-map-carousel-counter');
        if (counter) {
            counter.textContent = `${index + 1} / ${total}`;
        }

        // Hydrate the active slide (price, wishlist, links, width)
        this._hydrateSlot(popupEl, listing);

        // If we rendered a skeleton, fetch real content
        if (!this._popupHtmlCache.has(listing.id)) {
            this._fetchAndReplaceSlide(listing, index, this._carouselMarkerKey);
        }

        // Highlight corresponding list card
        const listingKey = getListingKey(listing);
        this.$store.search.hoveredListingId = listingKey;

        // Scroll corresponding list card into view
        const cardEl = document.getElementById(`listing-card-${listingKey}`);
        if (cardEl) {
            cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    },

    /**
     * Hydrate the current carousel slot (price, wishlist, links, width).
     */
    _hydrateSlot(popupEl, listing) {
        const slot = popupEl.querySelector('.search-map-carousel-slot');
        if (!slot) return;

        // Set card width
        const cardWidth = this.getCardWidth();
        const card = slot.querySelector('.search-map-popup-card');
        if (card && cardWidth) {
            card.style.width = `${cardWidth}px`;
        }

        // Update price
        const priceEl = slot.querySelector('[data-popup-price]');
        if (priceEl) {
            priceEl.textContent = formatPrice(getListingDisplayPrice(listing, this.$store.search));
        }

        const priceLabelEl = slot.querySelector('[data-popup-price-label]');
        if (priceLabelEl) {
            priceLabelEl.textContent = this.$store.search?.priceLabel || '/ night';
        }

        // Update wishlist state
        const listingId = listing.id || listing.guestyListingId;
        const heartBtn = slot.querySelector('.search-map-popup-heart');
        if (heartBtn) {
            const isFavorited = this.$store.wishlist?.has?.(listingId);
            heartBtn.classList.toggle('is-active', !!isFavorited);
        }

        // Append search query string to links
        const suffix = window.location.search;
        if (suffix) {
            slot.querySelectorAll('[data-popup-link]').forEach((link) => {
                const base = link.getAttribute('href').split('?')[0];
                link.setAttribute('href', base + suffix);
            });
        }
    },

    /**
     * Fetch a single slide's popup HTML, cache it, and update the slot if still active.
     */
    async _fetchAndReplaceSlide(listing, index, markerKey) {
        try {
            const html = await this.$store.search.fetchPopup(listing.id);
            this._popupHtmlCache.set(listing.id, html);

            // Only update DOM if this carousel is still open and on the same slide
            if (this._carouselMarkerKey !== markerKey) return;
            if (this._carouselIndex !== index) return;

            const popupEl = this.popup?.getElement();
            if (!popupEl) return;

            const slot = popupEl.querySelector('.search-map-carousel-slot');
            if (slot) {
                slot.innerHTML = html;
                this._hydrateSlot(popupEl, listing);
            }
        } catch (err) {
            console.error('Failed to load popup for carousel slide:', err);
        }
    },

    /**
     * Batch-prefetch all remaining slides in a single HTTP request.
     */
    async _batchPrefetchRemaining(listings, markerKey) {
        // Collect entry IDs that aren't already cached
        const uncachedIds = listings
            .filter(l => !this._popupHtmlCache.has(l.id) && !this._getPopupHtml(l))
            .map(l => l.id);

        if (uncachedIds.length === 0) return;

        try {
            const popups = await this.$store.search.fetchPopupBatch(uncachedIds);

            // Populate cache
            for (const [id, html] of Object.entries(popups)) {
                this._popupHtmlCache.set(parseInt(id, 10), html);
            }

            // If carousel is still open, update the active slide if it was waiting
            if (this._carouselMarkerKey !== markerKey) return;
            const activeListing = this._carouselListings?.[this._carouselIndex];
            if (activeListing && popups[String(activeListing.id)]) {
                const popupEl = this.popup?.getElement();
                if (!popupEl) return;
                const slot = popupEl.querySelector('.search-map-carousel-slot');
                if (slot && slot.querySelector('.animate-pulse')) {
                    slot.innerHTML = popups[String(activeListing.id)];
                    this._hydrateSlot(popupEl, activeListing);
                }
            }
        } catch (err) {
            console.error('Batch prefetch failed:', err);
        }
    },

    updateMarkerVisibility() {
        const source = this.map.getSource(SOURCE_ID);
        if (!source) return;

        // Query unclustered features to find which listings are visible
        const unclusteredFeatures = this.map.querySourceFeatures(SOURCE_ID, {
            filter: ['!', ['has', 'point_count']],
        });

        // Create a set of visible (unclustered) listing IDs
        const visibleIds = new Set(unclusteredFeatures.map((f) => f.properties.id));

        // Show/hide markers based on clustering state.
        // Use visibility (not display) so the element keeps its layout dimensions.
        for (const [markerKey, marker] of this.priceMarkersMap) {
            const el = marker.getElement();
            let isVisible;

            if (markerKey.startsWith('group_')) {
                // Group marker: visible if ANY member listing is unclustered
                const group = this.markerGroups.get(markerKey);
                isVisible = group?.listings.some((l) => visibleIds.has(getListingKey(l))) ?? false;
            } else {
                isVisible = visibleIds.has(markerKey);
            }

            if (isVisible) {
                el.style.visibility = '';
                el.style.pointerEvents = '';
            } else {
                el.style.visibility = 'hidden';
                el.style.pointerEvents = 'none';
            }
        }
    },

    clearPriceMarkers() {
        for (const marker of this.priceMarkersMap.values()) {
            marker.remove();
        }
        this.priceMarkersMap.clear();
    },

    updateMarkers(options = {}) {
        const listings = this.$store.search?.results?.mapPoints || [];

        if (!this.map || !this.loaded) return false;

        if (!this.isContainerUsable()) {
            this.pendingMarkers = true;
            this.pendingUpdateOptions = options;
            return false;
        }

        // Filter listings with valid coordinates and convert to GeoJSON
        const features = listings
            .map((listing) => {
                const lat = this.toNumber(listing.lat);
                const lng = this.toNumber(listing.lng);
                if (!isValidCoord(lng, lat)) return null;
                return {
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [lng, lat],
                    },
                    properties: {
                        id: getListingKey(listing),
                        title: listing.title,
                        slug: listing.slug,
                        basePrice: listing.basePrice || listing.prices?.basePrice,
                    },
                };
            })
            .filter(Boolean);

        // Update the source data
        const source = this.map.getSource(SOURCE_ID);
        if (source) {
            source.setData({
                type: 'FeatureCollection',
                features: features,
            });
        }

        // Create/update price markers
        this.createPriceMarkers(listings);

        // CRITICAL UX RULE: Only auto-fit for destination/filter/pagination searches
        // Never auto-fit if this was a bounds-based search (would be disorienting)
        // Check the source of the last search to determine intent
        const lastSource = this.$store.search?._lastSearchSource || 'user';
        if (!options.skipFit && (options.isInitial || lastSource !== 'bounds')) {
            this.queueFitBounds(features, options);
        }

        // Update visibility after a short delay to let clustering settle
        setTimeout(() => this.updateMarkerVisibility(), 100);

        return true;
    },

    getContainerSize() {
        const el = this.$refs.map;
        return {
            width: el?.clientWidth || 0,
            height: el?.clientHeight || 0,
        };
    },

    isContainerVisible() {
        const el = this.$refs.map;
        if (!el) return false;
        if (el.offsetParent === null) return false;
        return el.getClientRects().length > 0;
    },

    isContainerUsable() {
        const size = this.getContainerSize();
        return size.width > 0 && size.height > 0 && this.isContainerVisible();
    },

    isMapTransformUsable() {
        const t = this.map?.transform;
        if (!t) return false;
        return Number.isFinite(t.width)
            && Number.isFinite(t.height)
            && t.width > 0
            && t.height > 0;
    },

    waitForStableSize() {
        return new Promise((resolve) => {
            let last = null;
            let stableFrames = 0;

            const check = () => {
                const size = this.getContainerSize();
                if (size.width > 0 && size.height > 0 && this.isContainerVisible()) {
                    if (last && size.width === last.width && size.height === last.height) {
                        stableFrames += 1;
                    } else {
                        stableFrames = 0;
                    }
                    last = size;
                    if (stableFrames >= 2) {
                        resolve();
                        return;
                    }
                }
                requestAnimationFrame(check);
            };

            requestAnimationFrame(check);
        });
    },

    flushPendingMarkers() {
        if (!this.pendingMarkers) return;
        this.pendingMarkers = false;
        const options = this.pendingUpdateOptions || {};
        this.pendingUpdateOptions = null;

        const didUpdate = this.updateMarkers(options);
        if (didUpdate && this.pendingInitialFit) {
            this.hasInitialFit = true;
            this.pendingInitialFit = false;
            this.pendingInitialReveal = false;
        }
    },

    queueFitBounds(features, options = {}) {
        this.pendingFitId += 1;
        this.pendingFit = {
            id: this.pendingFitId,
            features,
            options,
            attempts: 0,
        };

        if (this.loaded) {
            this.flushPendingFit();
        }
    },

    async flushPendingFit() {
        if (!this.pendingFit || this.pendingFitInFlight) return;

        this.pendingFitInFlight = true;

        // Always use the latest queued fit (loop until no newer fit is pending)
        while (this.pendingFit) {
            const { id, features, options } = this.pendingFit;

            await this.waitForStableSize();

            // If a newer fit was queued while we waited, loop to use it instead
            if (!this.pendingFit || this.pendingFit.id !== id) {
                continue;
            }

            if (this.isContainerUsable()) {
                this.map.resize();
            } else {
                break;
            }

            if (!this.isMapTransformUsable()) {
                if (this.pendingFit && this.pendingFit.id === id) {
                    this.pendingFit.attempts += 1;
                    if (this.pendingFit.attempts <= 6) {
                        await new Promise(r => requestAnimationFrame(r));
                        continue;
                    }
                }
                break;
            }

            this.fitBounds(features, options);

            // Clear pending fit after execution
            if (this.pendingFit && this.pendingFit.id === id) {
                this.pendingFit = null;
            }
            break;
        }

        this.pendingFitInFlight = false;
    },

    fitBounds(features, options = {}) {
        const animate = options.animate !== undefined ? options.animate : true;

        if (!features.length) {
            // Reset to default view when no results
            this.programmaticMoveInProgress = true;
            this.allowNextBoundsSearch = false;

            if (animate) {
                this.map.flyTo({
                    center: this.defaultCenter,
                    zoom: this.defaultZoom,
                    animate: true
                });
            } else {
                this.map.jumpTo({
                    center: this.defaultCenter,
                    zoom: this.defaultZoom,
                });
            }

            // Clear flag based on animation status
            if (animate) {
                 this.map.once('moveend', () => {
                    this.programmaticMoveInProgress = false;
                    this.allowNextBoundsSearch = false;
                });
            } else {
                // Immediate reset if no animation
                this.programmaticMoveInProgress = false;
                this.allowNextBoundsSearch = false;
            }

            // Reveal map after initial fit attempt (even if empty)
            if (options.isInitial && this.$refs.map) {
                requestAnimationFrame(() => {
                    this.$refs.map.style.opacity = '1';
                });
            }
            return;
        }

        if (features.length === 1) {
            // Single result - center on it with reasonable zoom
            this.programmaticMoveInProgress = true;
            this.allowNextBoundsSearch = false;

            if (animate) {
                this.map.flyTo({
                    center: features[0].geometry.coordinates,
                    zoom: 14,
                    animate: true
                });
            } else {
                this.map.jumpTo({
                    center: features[0].geometry.coordinates,
                    zoom: 14,
                });
            }

            if (animate) {
                this.map.once('moveend', () => {
                    this.programmaticMoveInProgress = false;
                    this.allowNextBoundsSearch = false;
                });
            } else {
                 this.programmaticMoveInProgress = false;
                 this.allowNextBoundsSearch = false;
            }

            if (options.isInitial && this.$refs.map) {
                requestAnimationFrame(() => {
                    this.$refs.map.style.opacity = '1';
                });
            }
            return;
        }

        // Multiple results - fit bounds to show all
        this.programmaticMoveInProgress = true;
        this.allowNextBoundsSearch = false;

        const bounds = new mapboxgl.LngLatBounds();

        features.forEach((feature) => {
            bounds.extend(feature.geometry.coordinates);
        });

        const padding = { top: 60, bottom: 20, left: 20, right: 20 };
        const camera = this.map.cameraForBounds(bounds, {
            padding,
            maxZoom: 16,
        });
        if (!camera || !Number.isFinite(camera.zoom)
            || !Number.isFinite(camera.center?.lng)
            || !Number.isFinite(camera.center?.lat)
        ) {
            this.programmaticMoveInProgress = false;
            this.allowNextBoundsSearch = false;
            return;
        }

        if (animate) {
            this.map.fitBounds(bounds, {
                padding,
                maxZoom: 16,
                animate: true,
            });
        } else {
            this.map.jumpTo({
                center: camera.center,
                zoom: camera.zoom,
            });
        }

        // Reveal the map after the first initial fit to prevent a flash.
        if (options.isInitial && this.$refs.map) {
            requestAnimationFrame(() => {
                this.$refs.map.style.opacity = '1';
            });
        }

        if (animate) {
            this.map.once('moveend', () => {
                this.programmaticMoveInProgress = false;
                this.allowNextBoundsSearch = false;
            });
        } else {
             this.programmaticMoveInProgress = false;
             this.allowNextBoundsSearch = false;
        }

        // Fallback: clear flag only if we animated and didn't clear synchronously
        // (If animate is false, we already cleared it)
        if (animate) {
            setTimeout(() => {
                if (this.programmaticMoveInProgress) {
                    if (window.__AVARI_DEBUG_MAP_SEARCH) {
                        console.warn('[MAP] Programmatic flag stuck, clearing via timeout fallback');
                    }
                    this.programmaticMoveInProgress = false;
                    this.allowNextBoundsSearch = false;
                }
            }, 3000);
        }
    },

    destroy() {
        if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
        }
        if (this.boundsSearchTimeout) {
            clearTimeout(this.boundsSearchTimeout);
        }
        if (this.windowResizeHandler) {
            window.removeEventListener('resize', this.windowResizeHandler);
            this.windowResizeHandler = null;
        }
        if (this.popup) {
            this.popup.remove();
            this.popup = null;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.visibilityObserver) {
            this.visibilityObserver.disconnect();
            this.visibilityObserver = null;
        }
        this.clearPriceMarkers();
        this.markerGroups.clear();
        this.listingToMarkerKey.clear();
        this._popupHtmlCache.clear();
        this._carouselListings = null;
        this._carouselMarkerKey = null;
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    },
}));
